/* ──────────────────────────────────────────────────────────────────────
 *  DimensionMedalModal — Centered detail overlay for a dimension
 *
 *  Opens when a user clicks one of the 2×3 dimension medal cards on
 *  the Impact Signature panel. Shows the dimension's identity, current
 *  medal, what the dimension represents, and how to grow it.
 *
 *  Replaces the inline-expand and right-side-detail patterns for the
 *  medal-click flow (per design conversation 2026-06-something). The
 *  radar-label click still routes through toggleDimension and uses
 *  the existing slide-in right panel; that surface is unchanged.
 *
 *  Per CLAUDE.md:
 *    - Overlay background is opaque (composite of solid base + elevated
 *      tint — see the .modalCard rule in DimensionRadar.module.css).
 *    - All user-facing strings via useAppTranslation with defaultValue.
 *    - Logical CSS properties for RTL.
 *    - role="dialog" + aria-modal + Escape-to-close + focus restore.
 * ────────────────────────────────────────────────────────────────────── */

import React, { useEffect, useRef } from 'react';
import { X, Lock } from 'lucide-react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import DimensionMedal, { type TierLevel } from './DimensionMedal';
import type { DimKey } from './dimensionConfig';
import styles from './DimensionRadar.module.css';

/* ── Display copy ── */

const MEDAL_LONG_LABELS: Record<TierLevel, string> = {
  none: '—',
  bronze: 'BRONZE',
  silver: 'SILVER',
  gold: 'GOLD',
  platinum: 'PLATINUM',
};

/* ── Props ── */

interface DimensionMedalModalProps {
  /** When non-null, modal is open and shows this dimension. */
  dim: DimKey | null;
  /** Current tier for the dimension being shown. */
  tier: TierLevel;
  /** Dimension display name, e.g. "Mind". */
  name: string;
  /** Short descriptor under the name, e.g. "Understanding & Insight". */
  descriptor: string;
  /** Dimension tint CSS value, e.g. var(--dim-mind, #7ea3cc). */
  color: string;
  /** Paragraph of what the dimension represents. Concept copy — implies no credit. */
  meaning: string;
  /**
   * Activities that grow this dimension — ENABLED rows from the impact_activities
   * catalog (DEV-1480), supplied by the caller. Every entry is an act the emission
   * engine will actually credit.
   *
   * MAY BE EMPTY, and the modal renders no growth list when it is. Do not fall back
   * to hardcoded suggestions: a dimension with no wired acts has genuinely nothing
   * to offer yet, and saying otherwise would promise credit that never arrives.
   *
   * DEV-1489 Slice C — each item carries a `locked` flag. A locked item is a
   * next-rung tease: rendered muted, with a lock affix and the unlocking rung,
   * never as something the viewer can act on now (disable-and-explain).
   */
  growth?: GrowthItem[];
  /** Called when the user closes the modal (X, backdrop, or Escape). */
  onClose: () => void;
}

/** DEV-1489 — one growth line, gated by the viewer's rung. */
export interface GrowthItem {
  label: string;
  hint?: string;
  locked: boolean;
  /** Localized "unlocks at …" copy for a locked item. */
  lockLabel?: string;
}

/* ── Component ── */

export default function DimensionMedalModal({
  dim,
  tier,
  name,
  descriptor,
  color,
  meaning,
  growth,
  onClose,
}: DimensionMedalModalProps) {
  const { t } = useAppTranslation();

  /* Restore body scroll + focus after close. The opener (DimensionRadar)
     owns dim state; we just hook side effects on mount/unmount of an
     "open" instance. */
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!dim) return;

    // Save focus + lock body scroll
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Move focus to the close button so Escape / Tab navigation has an anchor
    queueMicrotask(() => closeBtnRef.current?.focus());

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      // Restore focus to the element that opened the modal
      previousFocusRef.current?.focus?.();
    };
  }, [dim, onClose]);

  if (!dim) return null;

  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const tierLabel = MEDAL_LONG_LABELS[tier];

  return (
    <div
      className={styles.modalOverlay}
      onClick={onBackdropClick}
      role="presentation"
    >
      <div
        className={styles.modalCard}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dim-medal-modal-title"
        style={{ '--dim-color': color } as React.CSSProperties}
      >
        <button
          ref={closeBtnRef}
          type="button"
          className={styles.modalCloseBtn}
          onClick={onClose}
          aria-label={t('common.close', 'Close')}
        >
          <X size={16} strokeWidth={2.4} />
        </button>

        <div className={styles.modalMedal}>
          <DimensionMedal tier={tier} dim={dim} ariaLabel={`${name} — ${tier}`} />
        </div>

        <h2 id="dim-medal-modal-title" className={styles.modalTitle}>
          {name}
        </h2>
        <div className={styles.modalSubtitle}>{descriptor}</div>
        <div className={`${styles.modalBadge} ${styles[`medalLabel_${tier}`]}`}>
          {tierLabel}
        </div>

        <div className={styles.modalSection}>
          <h4 className={styles.modalSectionHeading}>
            {t('impact.medalModal.character', 'What this represents')}
          </h4>
          <p className={styles.modalSectionBody}>{meaning}</p>
        </div>

        {growth && growth.length > 0 && (
          <div className={styles.modalSection}>
            <h4 className={styles.modalSectionHeading}>
              {t('impact.medalModal.activities', 'How to grow it')}
            </h4>
            <ul className={styles.modalGrowthList}>
              {growth.map((item, i) => (
                <li
                  key={i}
                  className={item.locked ? styles.growthLocked : undefined}
                  title={item.locked ? item.lockLabel : item.hint}
                >
                  {item.locked && <Lock size={12} className={styles.growthLockIcon} aria-hidden="true" />}
                  <span>{item.label}</span>
                  {item.locked && item.lockLabel && (
                    <span className={styles.growthLockLabel}>{item.lockLabel}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
